/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { CDNCacheOnloadMutation } from '@/components/content/CDNCacheOnLoadMutaion';
import { CustomerService } from '@/components/content/CustomerService';
import { Notifications } from '@/components/content/Notifications';
import { SelectedStoreSynchronizer } from '@/components/content/SelectedStoreSynchronizer';
import { SessionErrorDialog } from '@/components/content/SessionErrorDialog';
import { layoutManifest } from '@/components/layouts/manifest';
import { Meta } from '@/data/Meta';
import { useStaticSettings } from '@/data/Settings';
import { DpCatalogoProvider } from '@/data/context/dpcatalogo';
import { SettingProvider } from '@/data/context/setting';
import { Layout } from '@/data/types/Layout';
import { ThemeSettingsProvider } from '@/styles/theme';
import { CssBaseline, SxProps } from '@mui/material';
import { Theme, ThemeProvider } from '@mui/material/styles';
import Head from 'next/head';
import { FC, useMemo } from 'react';

type PageProps = {
	meta: Meta;
	layout: Layout;
	theme: Theme;
	additives?: { [key: string]: SxProps | ((theme: Theme) => SxProps) };
};

const DEFAULT_OG_IMAGE =
	'https://dpjye2wk9gi5z.cloudfront.net/wcsstore/DpCatalogo/apple-touch-icon-192x192.png';

export const PageBlock: FC<PageProps> = ({ meta, layout, theme, additives }) => {
	const Layout = layout && layoutManifest[layout.name] ? layoutManifest[layout.name] : () => null;
	const themeAdditives = useMemo(() => ({ additives }), [additives]);

	return (
		<>
			<Head>
				<title>{meta?.title}</title>
				<meta name="description" content={meta?.description} />
				<meta name="keywords" content={meta?.keywords} />
				<link
					rel="icon"
					href="https://dpjye2wk9gi5z.cloudfront.net/wcsstore/DpCatalogo/favicon.ico"
				/>
				{meta.canonical ? <link rel="canonical" href={meta.canonical} /> : null}
				<meta key="og:title" property="og:title" content={meta?.title} />
				<meta key="og:description" property="og:description" content={meta?.description} />
				<meta key="og:image" property="og:image" content={DEFAULT_OG_IMAGE} />
				<meta key="og:type" property="og:type" content="website" />
				<meta key="og:url" property="og:url" content={meta?.canonical} />
			</Head>
			<CustomerService />
			<CDNCacheOnloadMutation />
			<SelectedStoreSynchronizer />
			<ThemeProvider theme={theme}>
				<CssBaseline />
				<ThemeSettingsProvider value={themeAdditives}>
					<DpCatalogoProvider>
						<Layout layout={layout} />
						<Notifications />
						<SessionErrorDialog />
					</DpCatalogoProvider>
				</ThemeSettingsProvider>
			</ThemeProvider>
		</>
	);
};

export const StaticPageBlock: FC<PageProps> = ({ meta, layout, theme, additives }) => {
	const { settings } = useStaticSettings();

	return settings ? (
		<SettingProvider value={settings}>
			<PageBlock meta={meta} layout={layout} theme={theme} additives={additives} />
		</SettingProvider>
	) : null;
};
